// services/auth/RetryStrategy.ts
// Retry Pattern с Exponential Backoff

import { consola } from 'consola'

// Logger
const logger = consola.withTag('retry-strategy')

/**
 * Конфигурация retry
 */
export interface RetryConfig {
  maxRetries: number
  baseDelay: number
  maxDelay: number
  backoffMultiplier: number
  retryableStatuses: number[]
}

/**
 * Результат попытки
 */
interface RetryAttempt {
  attempt: number
  delay: number
  error?: any
}

/**
 * RetryStrategy - умные повторные попытки с увеличивающейся задержкой
 * 
 * Решает проблемы:
 * - Временные сбои сети
 * - Перегрузка сервера
 * - Rate limiting на сервере
 */
export class RetryStrategy {
  private config: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,      // 1 секунда
    maxDelay: 16_000,      // 16 секунд
    backoffMultiplier: 2,  // Удвоение задержки
    retryableStatuses: [408, 429, 500, 502, 503, 504],
  }
  
  // История попыток
  private attemptHistory: Map<string, RetryAttempt[]> = new Map()
  
  constructor(config?: Partial<RetryConfig>) {
    if (config) {
      this.config = { ...this.config, ...config }
    }
    logger.info('Инициализация', this.config)
  }
  
  /**
   * Выполнить функцию с retry
   */
  async execute<T>(
    fn: () => Promise<T>,
    options: {
      requestId?: string
      onRetry?: (attempt: number, delay: number) => void
    } = {},
  ): Promise<T> {
    const requestId = options.requestId || this.generateRequestId()
    this.attemptHistory.set(requestId, [])
    
    let lastError: any
    
    for (let attempt = 0; attempt <= this.config.maxRetries; attempt++) {
      try {
        // Первая попытка - без задержки
        if (attempt > 0) {
          const delay = this.calculateDelay(attempt)
          
          logger.info(`Попытка ${attempt}/${this.config.maxRetries}, задержка ${delay}ms`)
          
          // Сохраняем в историю
          this.recordAttempt(requestId, attempt, delay)
          
          // Вызываем callback
          if (options.onRetry) {
            options.onRetry(attempt, delay)
          }
          
          // Ждем перед повтором
          await this.delay(delay)
        }
        
        // Выполняем функцию
        const result = await fn()
        
        // Успех - очищаем историю
        this.attemptHistory.delete(requestId)
        
        if (attempt > 0) {
          logger.success(`Успех после ${attempt} попыток`)
        }
        
        return result
      } catch (error: any) {
        lastError = error
        
        // Проверяем, можно ли повторить
        if (!this.isRetryable(error)) {
          logger.error('Ошибка не подлежит повтору', error)
          throw error
        }
        
        // Это последняя попытка?
        if (attempt === this.config.maxRetries) {
          logger.error(`Исчерпаны все ${this.config.maxRetries} попытки`)
          this.recordAttempt(requestId, attempt, 0, error)
          throw error
        /* v8 ignore next */
        }
      /* v8 ignore next */
      }
      /* v8 ignore next */
    }
    
    // Не должны сюда попасть, но на всякий случай
    /* v8 ignore next */
    throw lastError
  }
  
  /**
   * Рассчитать задержку с exponential backoff
   */
  private calculateDelay(attempt: number): number {
    // Exponential backoff: baseDelay * (multiplier ^ attempt)
    const exponentialDelay = this.config.baseDelay * Math.pow(this.config.backoffMultiplier, attempt - 1)
    
    // Добавляем jitter (случайность) для избежания thundering herd
    const jitter = Math.random() * 0.3 * exponentialDelay
    
    // Ограничиваем максимальной задержкой
    const delay = Math.min(exponentialDelay + jitter, this.config.maxDelay)
    
    return Math.round(delay)
  }
  
  /**
   * Проверить, можно ли повторить запрос
   */
  private isRetryable(error: any): boolean {
    // Нет ответа от сервера (network error)
    if (!error.status && !error.statusCode) {
      return true
    }
    
    // Проверяем статус код
    const status = error.status || error.statusCode
    return this.config.retryableStatuses.includes(status)
  }
  
  /**
   * Задержка
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
  
  /**
   * Записать попытку в историю
   */
  private recordAttempt(requestId: string, attempt: number, delay: number, error?: any): void {
    /* v8 ignore next */
    const history = this.attemptHistory.get(requestId) || []
    /* v8 ignore next */
    /* v8 ignore next */
    history.push({ attempt, delay, error })
    this.attemptHistory.set(requestId, history)
  }
  
  /**
   * Получить историю попыток
   */
  public getAttemptHistory(requestId: string): RetryAttempt[] {
    return this.attemptHistory.get(requestId) || []
  }
  
  /**
   * Очистить историю
   */
  public clearHistory(): void {
    this.attemptHistory.clear()
  }
  
  /**
   * Генерировать ID запроса
   */
  private generateRequestId(): string {
    return `retry_${Date.now()}_${Math.random().toString(36).slice(7)}`
  }
  
  /**
   * Получить статистику
   */
  public getStats(): {
    totalRequests: number
    activeRetries: number
  } {
    return {
      totalRequests: this.attemptHistory.size,
      activeRetries: [...this.attemptHistory.values()].filter(h => h.length > 0).length,
    }
  }
  
  /**
   * Обновить конфигурацию
   */
  public updateConfig(config: Partial<RetryConfig>): void {
    this.config = { ...this.config, ...config }
    logger.info('Конфигурация обновлена', this.config)
  }
}

// Экспортируем единственный экземпляр
export const retryStrategy = new RetryStrategy()

